import { withScope } from '@/contexts/ScopeContext';
import { translateToTz } from '@/util/dateTime';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import format from 'date-fns/format';
import React from 'react';
import ospitek_logo_blue from '@/assets/images/ospitek_logo_blue.svg';
import ospitek_logo_white from '@/assets/images/ospitek_logo_white.svg';
import Filters from '../pages/kiosk/schedule/Filters';
import { Box } from '@material-ui/core';
import { useCurrentTime } from '@/util/timerHooks';

interface HospitalInfoProps {
  scope: any;
  date?: Date;
  onDateChange?: (date: Date) => void;
  onClearDate?: () => void;
}

const WallClock = ({ translateToHospitalZone }) => {
  const classes = useStyles();

  // Use the proper useCurrentTime hook for real-time clock display with minute precision
  const date = useCurrentTime({
    interval: 60000, // Update every minute
    precision: 'minute',
    batteryOptimized: true, // Optimize for battery life since it's just a display clock
  });

  return (
    <Typography className={classes.hospitalDateAndTime}>
      {format(translateToHospitalZone(date), 'MMMM D • HH:mm')}
    </Typography>
  );
};

export const HospitalInfo = ({ scope, date, onDateChange, onClearDate }: HospitalInfoProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const logo = theme.palette.type === 'light' ? ospitek_logo_blue : ospitek_logo_white;

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" flex={1}>
        <img className={classes.logo} src={logo} alt="Ospitek logo" />
      </Box>
      <Typography className={classes.hospitalName}>{scope?.hospital?.name}</Typography>
      <Box display="flex" alignItems="center" flex={1}>
        {date && onDateChange ? (
          <Filters date={date} withoutPhysician onChange={onDateChange} onClearDate={onClearDate} />
        ) : (
          <WallClock translateToHospitalZone={translateToTz(scope?.hospital?.timezone?.id)} />
        )}
      </Box>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1.25rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    padding: '0 2.5rem 0.5rem',
    '@media (max-height: 1079px)': {
      fontSize: '1rem',
      marginTop: '0.25rem',
      marginBottom: '0.25rem',
      padding: '0 2.5rem 0.25rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '.75rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  logo: {
    width: '6em',
  },
  hospitalName: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 4,
    textAlign: 'center',
    fontSize: '1.75rem',
    fontWeight: 500,
    '@media (max-height: 1079px)': {
      fontSize: '1rem',
    },
    [theme.breakpoints.down('md')]: {
      flex: 2,
      textAlign: 'right',
    },
    '@media (max-width: 767px)': {
      fontSize: '1rem',
    },
  },
  hospitalDateAndTime: {
    flex: 1,
    fontSize: '1.75rem',
    textAlign: 'right',
    '@media (max-height: 1079px)': {
      fontSize: '1rem',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

export default withScope(HospitalInfo);
